import React, { useCallback } from 'react';

import { getDefault, readCMSValue, buildCMSStyles } from 'modules/shared-cms/utils';
import { TextColorProps } from 'modules/shared-cms/cms-base-type';
import { CMSImageContainer } from 'modules/shared-cms/components';
import { HtmlContainer } from 'modules/shared-ui/components';
import { Preference, OptinPreferenceValues, OptinTypeValues } from 'modules/shared/models';

import { DescriptionContainer, MentionContainer } from 'modules/shared-cms-advanced';
import { useRouter } from 'modules/shared/hooks';
import { formatTechDate } from 'modules/shared/utils';
import { GameForm, Participation } from './game-form';
import { GameProps } from './game-type';

import enhancer from './game-enhancer';

import styles from './game-page.module.css';

const InnerGamePage: React.FC<GameProps> = React.memo(({ apiGame, cmsGame, createParticipation }: GameProps) => {
    const {
        picture = cmsGame.picture,
        title = getDefault(readCMSValue(cmsGame.title), ''),
        titleTextColor = getDefault(readCMSValue(cmsGame.titleTextColor), '#000000'),
        subTitle = getDefault(readCMSValue(cmsGame.subTitle), ''),
        subTitleTextColor = getDefault(readCMSValue(cmsGame.subTitleTextColor), '#000000'),
        formTextColor = getDefault(readCMSValue(cmsGame.formTextColor), '#000000'),
        mentions = getDefault(readCMSValue(cmsGame.mentions), ''),
        logo = getDefault(readCMSValue(cmsGame.logo), []),
        partnerLogo = getDefault(readCMSValue(cmsGame.partnerLogo), []),
    } = {};

    const gameClosedMsgProps = {
        description: cmsGame.finishedGameText,
        descriptionTextColor: cmsGame.finishedGameTextColor,
        descriptionFontSize: cmsGame.finishedGameFontSize,
    };

    const { history } = useRouter();

    const handleCreateParticipation = useCallback(
        (participation: Participation) => {
            const preferences = [] as Preference[];
            let checked = false;
            Object.entries(participation.subscriptions).forEach(e => {
                const key = e[0];
                const value = e[1];
                if (value) {
                    checked = true;
                }
                const id = OptinPreferenceValues.getIntByString(key);
                if (id !== undefined) {
                    preferences.push({
                        id,
                        checked: value,
                    });
                }
            });

            createParticipation({
                gameId: apiGame.id,
                eventDate: formatTechDate(participation.eventDate || new Date()),
                optins: [
                    {
                        id: OptinTypeValues.PRINTEMPS_LISTES,
                        checked,
                        preferences,
                    },
                ],
                person: participation,
            }).then(() => {
                history.push(`/mariage/jeu-participation-confirmee/${apiGame.id}`);
            });
        },
        [apiGame, createParticipation],
    );

    const content = (
        <div className={styles.textContainer}>
            <div style={buildCMSStyles<TextColorProps>({ textColor: titleTextColor })}>
                <HtmlContainer htmlContent={title} className={styles.title} />
            </div>
            {apiGame.isOpen ? (
                <React.Fragment>
                    <div style={buildCMSStyles<TextColorProps>({ textColor: subTitleTextColor })}>
                        <HtmlContainer htmlContent={subTitle} className={styles.subTitle} />
                    </div>
                    <div className={styles.formContainer}>
                        <GameForm onSuccess={handleCreateParticipation} textColor={formTextColor} />
                    </div>
                </React.Fragment>
            ) : (
                <React.Fragment>
                    <DescriptionContainer {...gameClosedMsgProps} />
                </React.Fragment>
            )}
        </div>
    );

    return (
        <div className={styles.container}>
            <React.Fragment>
                <div className={styles.innerFormContainer}>
                    {picture && picture.iv.length > 0 && (
                        <div className={styles.imageContainer}>
                            <CMSImageContainer picture={picture} style={{ width: '100%' }} />
                        </div>
                    )}
                    <div className={styles.contentContainer}>{content}</div>
                </div>
                <div className={styles.logoContainer}>
                    {logo.length > 0 && (
                        <div className={styles.logo}>
                            <CMSImageContainer picture={logo} style={{ width: '100%' }} />
                        </div>
                    )}
                    {partnerLogo.length > 0 && (
                        <div className={styles.logo}>
                            <CMSImageContainer picture={partnerLogo} style={{ width: '100%' }} />
                        </div>
                    )}
                </div>
                <div className={styles.mentionContainer}>
                    <MentionContainer mention={mentions} mentionFontSize={10} mentionTextColor="#000000" />
                </div>
            </React.Fragment>
        </div>
    );
});

export const GamePage = enhancer(InnerGamePage);
